import { Box, SxProps, Theme, Tooltip } from '@mui/material';
import { keyframes } from '@mui/system';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

// Holds at each end for a moment, then glides to the other.
const glide = keyframes`
  0%, 15% { transform: translateX(0); }
  85%, 100% { transform: translateX(var(--marquee-shift)); }
`;

/** Pixels a second the text moves at, slow enough to read along. */
const SPEED = 40;

interface MarqueeTextProps {
  children: ReactNode;
  /** The whole text, for the tooltip when motion is reduced and the text is cut. */
  title: string;
  sx?: SxProps<Theme>;
}

/**
 * One line of text that slides back and forth when it does not fit, and stays still when it does.
 * Hovering pauses it; with reduced motion it is cut with an ellipsis and shown whole in a tooltip.
 */
const MarqueeText = ({ children, title, sx }: MarqueeTextProps) => {
  const outer = useRef<HTMLSpanElement>(null);
  const inner = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(0);

  useLayoutEffect(() => {
    const measure = () => setOverflow(Math.max(0, (inner.current?.scrollWidth ?? 0) - (outer.current?.clientWidth ?? 0)));
    measure();
    if (typeof ResizeObserver === 'undefined' || !outer.current) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(outer.current);
    return () => observer.disconnect();
  }, [children]);

  const moving = overflow > 0;
  const seconds = Math.max(4, (overflow / SPEED) * 2);
  return (
    <Tooltip title={moving ? title : ''}>
      {/* width 0 with flex-grow: the box takes only the room left, since a nowrap text's own width would otherwise widen the page. */}
      <Box component="span" ref={outer} data-testid="marquee" data-overflow={overflow} sx={[{ display: 'block', width: 0, flexGrow: 1, minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap' }, ...(Array.isArray(sx) ? sx : [sx])]}>
        <Box
          component="span"
          ref={inner}
          sx={{
            display: 'inline-block',
            '--marquee-shift': `-${overflow}px`,
            animation: moving ? `${glide} ${seconds}s ease-in-out infinite alternate` : 'none',
            '&:hover': { animationPlayState: 'paused' },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }
          }}
        >
          {children}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default MarqueeText;
